import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import { useQuery } from '@tanstack/react-query';
import {
  DriverOrInvite,
  CustomerWithDriverOnboarding,
  DriverInviteStatus,
} from '../../../../../types/apiTypes';
import { Truck } from '../../../../../types/dbTypes';
import { formatUSPhoneForDisplay } from '../../../../../utils/phone';
import {
  isPreActiveCustomer,
  getDemoDriversAndInvites,
  getDemoTrucks,
} from '../home/utils/demoData';
import { DemoDataAlert } from '../home/components/DemoDataAlert';
import {
  UsersThree,
  MagnifyingGlass,
  X,
  Envelope,
  DotsThreeVertical,
  Truck as TruckIcon,
  Trash,
} from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import { useState, useRef, useEffect, useMemo } from 'react';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { InviteDriverModal } from './widgets/InviteDriverModal';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Modal, Select } from 'antd';
import { HttpStatusCode } from 'axios';

interface DriversHomeProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function DriversHome({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: DriversHomeProps) {
  const { screenView } = useScreenView();
  const { userId, userRecord } = useUser();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isChangeTruckModalOpen, setIsChangeTruckModalOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedTruckId, setSelectedTruckId] = useState<string>('');
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  // ============================================================================
  // Pre-Active Customer Detection
  // ============================================================================

  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  const { data: driversAndInvites = [], isLoading: loadingDriversAndInvites } =
    useQuery({
      queryKey: ['driversAndInvites', userId ?? '', searchKeyword],
      queryFn: async () => {
        const response = await callProtectedApi(
          `/driverInvites/${userId ?? ''}`,
          'post',
          {
            searchKeyword:
              searchKeyword && searchKeyword?.length > 0
                ? searchKeyword
                : undefined,
          }
        );
        return response?.data?.data?.driversAndInvites || [];
      },
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000, // 15 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
    });

  const { data: trucks = [], isLoading: loadingTrucks } = useQuery({
    queryKey: ['trucks', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/trucks/list/${userId ?? ''}`,
        'get',
        {},
        'Error occurred while fetching your trucks.'
      );
      return response?.data?.data?.trucks || [];
    },
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  const displayDriversAndInvites = useMemo(() => {
    if (isPreActive) {
      // For pre-active customers: combine real owner data with demo drivers/invites
      const demoDriversOnly = getDemoDriversAndInvites();

      // Get real owner data from userRecord
      const realOwnerDriver = (userRecord as CustomerWithDriverOnboarding)
        ?.driver;

      if (
        realOwnerDriver &&
        realOwnerDriver.first_name &&
        realOwnerDriver.last_name &&
        realOwnerDriver.email
      ) {
        // Get demo trucks to find a default truck for the owner
        const demoTrucks = getDemoTrucks();
        const defaultTruckId = demoTrucks[0]?.truck_id || 'demo-truck-1';

        // Construct owner entry from real driver data with proper type safety
        const ownerEntry: DriverOrInvite = {
          object_id: realOwnerDriver.driver_id,
          first_name: realOwnerDriver.first_name,
          last_name: realOwnerDriver.last_name,
          email: realOwnerDriver.email,
          phone_number: realOwnerDriver.phone_number ?? null,
          truck_id: defaultTruckId, // Use first demo truck as owner's truck
          status: DriverInviteStatus.ACTIVE,
          is_driver: true,
          is_primary_owner: true,
          updated_at: realOwnerDriver.updated_at || new Date(),
        };

        // Return owner first, then demo drivers/invites
        return [ownerEntry, ...demoDriversOnly];
      }

      // Fallback to demo drivers only if no real owner data or missing required fields
      return demoDriversOnly;
    }

    // For active customers, return real data
    return driversAndInvites;
  }, [isPreActive, driversAndInvites, userRecord]);

  const displayTrucks = useMemo(() => {
    return isPreActive ? getDemoTrucks() : trucks;
  }, [isPreActive, trucks]);

  // Loading states: Never show loading for demo data
  const displayLoadingDriversAndInvites = isPreActive
    ? false
    : loadingDriversAndInvites;
  const displayLoadingTrucks = isPreActive ? false : loadingTrucks;

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [loadingDeleteMap, setLoadingDeleteMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [loadingResendMap, setLoadingResendMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [loadingCancelMap, setLoadingCancelMap] = useState<
    Map<string, boolean>
  >(new Map());
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const togglePopup = (driverId?: string) => {
    if (!driverId) return;
    setOpenMenuId((prev) => (prev === driverId ? null : driverId));
  };

  const handleSetLoadingDelete = (loading: boolean, driverId: string) => {
    setLoadingDeleteMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(driverId, loading);
      return newMap;
    });
  };

  const handleSetLoadingResend = (loading: boolean, driverId: string) => {
    setLoadingResendMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(driverId, loading);
      return newMap;
    });
  };
  const handleSetLoadingCancel = (loading: boolean, driverId: string) => {
    setLoadingCancelMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(driverId, loading);
      return newMap;
    });
  };

  const removeDriver = async (customerId: string, driverId: string) => {
    // Prevent action for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to manage drivers');
      return;
    }
    handleSetLoadingDelete(true, driverId);
    try {
      const response = await callProtectedApi(
        `/drivers/delete/${customerId}/${driverId}`,
        'delete',
        {},
        'Error occurred while removing driver.'
      );
      if (response?.status === 200) {
        toast.success('Driver removed successfully');
        // Refetch the drivers list
        await queryClient.invalidateQueries({
          queryKey: ['driversAndInvites'],
        });
      }
    } catch (e: any) {
      console.error('Error occurred while removing driver: ', e);
    } finally {
      handleSetLoadingDelete(false, driverId);
    }
  };

  const changeDriverTruck = async (
    customerId: string,
    driverId: string,
    truckId: string
  ) => {
    // Prevent action for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to manage drivers');
      return;
    }
    handleSetLoadingDelete(true, driverId);
    try {
      const response = await callProtectedApi(
        `/drivers/truck/${customerId}`,
        'patch',
        {
          driver_id: driverId,
          truck_id: truckId,
        },
        'Error occurred while updating driver truck.'
      );
      if (response?.status === 200) {
        toast.success('Driver truck updated successfully');
        // Refetch the drivers list
        await queryClient.invalidateQueries({
          queryKey: ['driversAndInvites'],
        });
      }
    } catch (e: any) {
      console.error('Error occurred while updating driver truck: ', e);
    } finally {
      handleSetLoadingDelete(false, driverId);
    }
  };

  const handleChangeTruck = () => {
    if (selectedTruckId && selectedDriverId) {
      changeDriverTruck(userId ?? '', selectedDriverId, selectedTruckId);
      setIsChangeTruckModalOpen(false);
    } else {
      toast.error('Please select a truck');
    }
  };

  const openChangeTruckModal = (driverId: string) => {
    setSelectedDriverId(driverId);
    setSelectedTruckId('');
    setIsChangeTruckModalOpen(true);
  };
  const handleClickOutside = (event: MouseEvent) => {
    let clickedInsideAny = false;

    displayDriversAndInvites.forEach((driver: DriverOrInvite) => {
      const id = driver?.object_id ?? '';
      const popupRef = popupRefs.current[id];
      const buttonRef = buttonRefs.current[id];

      if (
        (popupRef && popupRef.contains(event.target as Node)) ||
        (buttonRef && buttonRef.contains(event.target as Node))
      ) {
        clickedInsideAny = true;
      }
    });

    if (!clickedInsideAny) setOpenMenuId(null);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSearchBar = () => (
    <div
      className={`flex h-[48px] max-h-[48px] w-full min-w-[180px] flex-grow items-center gap-1 rounded-full bg-white px-4 py-3 text-[#7D8191] lg:w-[40%] lg:max-w-[40%]`}
    >
      <MagnifyingGlass size={20} />
      <input
        type="text"
        placeholder="Search"
        className="ml-2 flex-grow bg-transparent text-gray-600 outline-none disabled:cursor-not-allowed"
        value={searchKeyword}
        onChange={(e) => {
          setSearchKeyword(e.target.value);
        }}
        disabled={isPreActive}
      />
      {searchKeyword && searchKeyword?.length > 0 && (
        <button
          className="rounded-full p-[2px] hover:bg-gray-200"
          onClick={() => setSearchKeyword('')}
        >
          <X size={20} />
        </button>
      )}
    </div>
  );

  const sendInvite = async ({
    customer_id,
    truck_id,
    email,
    first_name,
    last_name,
    updated_at,
  }: {
    customer_id: string;
    truck_id: string;
    email: string;
    first_name: string;
    last_name: string;
    updated_at: Date;
  }) => {
    // Prevent action for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to invite drivers');
      return;
    }
    try {
      const response = await callProtectedApi(
        `/driverInvites`,
        'post',
        {
          truck_id: truck_id,
          customer_id: customer_id,
          email: email,
          first_name: first_name,
          last_name: last_name,
        },
        'Error occurred while sending driver invite.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Successfully resent driver invite.');
        await queryClient.refetchQueries({
          queryKey: ['driversAndInvites', userId],
          type: 'active',
        });
      }
    } catch (e: any) {
      console.error('Error occurred while resending driver invite: ', e);
    }
  };
  const cancelInvite = async (invite_id: string) => {
    // Prevent action for demo data
    if (isPreActive) {
      toast.error('Complete onboarding to manage invites');
      return;
    }
    try {
      const response = await callProtectedApi(
        `/driverInvites/cancel`,
        'post',
        {
          invite_id: invite_id,
        },
        'Error occurred while canceling driver invite.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Successfully canceled driver invite.');
        await queryClient.refetchQueries({
          queryKey: ['driversAndInvites', userId],
          type: 'active',
        });
      }
    } catch (e: any) {
      console.error('Error occurred while canceling driver invite: ', e);
    }
  };

  const Badge = ({
    className = '',
    children,
  }: {
    className?: string;
    children: React.ReactNode;
  }) => (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-[2px] text-[12px] font-medium leading-5 ${className}`}
    >
      {children}
    </span>
  );

  const getStatusBadge = (d: DriverOrInvite) => {
    if (d?.is_primary_owner) {
      return {
        label: 'Owner',
        className: 'border-blue-800 bg-blue-100 text-blue-800 ',
      };
    }
    // If not owner and not active -> pending invite
    if (d?.status !== 'active') {
      return {
        label: 'Pending',
        className: 'border-yellow-600 bg-yellow-100 text-yellow-800 ',
      };
    }
    // Active non-owner => driver
    return {
      label: 'Driver',
      className: 'border-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen',
    };
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center overflow-x-hidden overflow-y-hidden">
            <div className="flex h-full flex-col overflow-x-hidden overflow-y-hidden sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
              <div className="flex h-full flex-col gap-y-4 overflow-y-auto pt-4">
                {isPreActive && <DemoDataAlert />}
                <div className="flex w-full flex-col justify-between sm:h-[112px] sm:max-h-[112px] sm:gap-y-4 lg:h-[48px] lg:flex-row lg:gap-y-0">
                  {renderSearchBar()}
                  <AccentButtonOutline
                    addClass="h-[48px] max-h-[48px] rounded-full py-[14px] px-4 space-x-1 w-[196px] disabled:cursor-not-allowed"
                    onClick={() => setIsInviteModalOpen(true)}
                    disabled={isPreActive}
                  >
                    <Envelope size={20} />
                    <p>Invite Driver</p>
                  </AccentButtonOutline>
                </div>
                {!displayLoadingDriversAndInvites && !displayLoadingTrucks ? (
                  <>
                    {displayDriversAndInvites &&
                    displayDriversAndInvites?.length > 0 ? (
                      <div className="flex h-full w-full flex-col overflow-x-auto rounded-[8px] border-[1px] border-bordergray bg-white">
                        <table className="w-full min-w-full overflow-x-auto bg-white">
                          <thead className="h-[50px] max-h-[50px] bg-primarybg">
                            <tr className="h-[50px] max-h-[50px] overflow-hidden">
                              <th className="h-[50px] max-h-[50px] items-center overflow-hidden px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                First Name
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Last Name
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary sm:w-[40%] lg:w-auto">
                                Email
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Phone
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Truck
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Role / Status
                              </th>
                              <th className="h-[50px] max-h-[50px] w-[25%] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                            </tr>
                          </thead>
                          <tbody className="table-fixed overflow-x-auto bg-white">
                            {displayDriversAndInvites.map(
                              (driver: DriverOrInvite, index: number) => (
                                <tr
                                  key={index}
                                  className="h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200"
                                >
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {driver?.first_name}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {driver?.last_name}
                                  </td>
                                  <td
                                    className={`flex h-[50px] max-h-[50px] w-[30%] items-center justify-start px-4 py-2 text-[14px]`}
                                  >
                                    {driver?.email}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden whitespace-nowrap px-4 py-2 text-[14px] text-primary">
                                    {driver?.phone_number
                                      ? formatUSPhoneForDisplay(
                                          driver.phone_number
                                        )
                                      : '—'}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {
                                      displayTrucks.find(
                                        (truck: Truck) =>
                                          truck.truck_id === driver?.truck_id
                                      )?.truck_display_name
                                    }
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2">
                                    {(() => {
                                      const { label, className } =
                                        getStatusBadge(driver);
                                      return (
                                        <Badge className={className}>
                                          {label}
                                        </Badge>
                                      );
                                    })()}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] w-[20%] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    <div className="flex h-full w-full items-center justify-center">
                                      {!loadingDeleteMap.get(
                                        driver?.object_id
                                      ) ? (
                                        <button
                                          ref={(el) => {
                                            buttonRefs.current[
                                              driver?.object_id ?? ''
                                            ] = el;
                                          }}
                                          onClick={() =>
                                            togglePopup(driver?.object_id ?? '')
                                          }
                                          className="relative disabled:cursor-not-allowed"
                                          disabled={isPreActive}
                                        >
                                          <DotsThreeVertical
                                            size={26}
                                            weight="bold"
                                          />
                                          {openMenuId ===
                                            (driver?.object_id ?? '') &&
                                            createPortal(
                                              <div
                                                ref={(el) => {
                                                  popupRefs.current[
                                                    driver?.object_id ?? ''
                                                  ] = el;
                                                }}
                                                onMouseDown={(e) =>
                                                  e.stopPropagation()
                                                }
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="fixed z-[9999] w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                                                style={{
                                                  left:
                                                    buttonRefs.current[
                                                      driver?.object_id ?? ''
                                                    ]?.getBoundingClientRect()
                                                      .left ?? 0,
                                                  top:
                                                    buttonRefs.current[
                                                      driver?.object_id ?? ''
                                                    ]?.getBoundingClientRect()
                                                      .top ?? 0,
                                                  transform:
                                                    'translateX(-100%) translateX(5px)',
                                                }}
                                              >
                                                {driver?.is_driver ? (
                                                  <div>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        // open first
                                                        openChangeTruckModal(
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                        // then close menu next tick to avoid race
                                                        setTimeout(
                                                          () =>
                                                            togglePopup(
                                                              driver?.object_id ??
                                                                ''
                                                            ),
                                                          0
                                                        );
                                                      }}
                                                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                                    >
                                                      <TruckIcon size={20} />
                                                      <p>Change Truck</p>
                                                    </button>
                                                    {!driver?.is_primary_owner && (
                                                      <button
                                                        onClick={async (
                                                          e: any
                                                        ) => {
                                                          e.stopPropagation();
                                                          await removeDriver(
                                                            userId ?? '',
                                                            driver?.object_id ??
                                                              ''
                                                          );
                                                          togglePopup(
                                                            driver?.object_id ??
                                                              ''
                                                          );
                                                        }}
                                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                                      >
                                                        <Trash size={20} />
                                                        <p>Remove Driver</p>
                                                      </button>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div>
                                                    <button
                                                      onClick={async (
                                                        e: any
                                                      ) => {
                                                        e.stopPropagation();
                                                        handleSetLoadingResend(
                                                          true,
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                        await sendInvite({
                                                          customer_id:
                                                            userId ?? '',
                                                          truck_id:
                                                            driver?.truck_id ??
                                                            '',
                                                          email:
                                                            driver?.email ?? '',
                                                          first_name:
                                                            driver?.first_name ??
                                                            '',
                                                          last_name:
                                                            driver?.last_name ??
                                                            '',
                                                          updated_at:
                                                            driver?.updated_at ??
                                                            new Date(),
                                                        });
                                                        handleSetLoadingResend(
                                                          false,
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                        togglePopup(
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                      }}
                                                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                                      disabled={loadingResendMap.get(
                                                        driver?.object_id
                                                      )}
                                                    >
                                                      {loadingResendMap.get(
                                                        driver?.object_id
                                                      ) ? (
                                                        <ClipLoader
                                                          color="#1D213B"
                                                          loading={true}
                                                          size={16}
                                                          aria-label="Loading Spinner"
                                                          data-testid="loader"
                                                        />
                                                      ) : (
                                                        <Envelope size={20} />
                                                      )}
                                                      <p>
                                                        {loadingResendMap.get(
                                                          driver?.object_id
                                                        )
                                                          ? 'Sending...'
                                                          : 'Resend Invite'}
                                                      </p>
                                                    </button>
                                                    <button
                                                      onClick={async (
                                                        e: any
                                                      ) => {
                                                        e.stopPropagation();
                                                        handleSetLoadingCancel(
                                                          true,
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                        await cancelInvite(
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                        handleSetLoadingCancel(
                                                          false,
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                        togglePopup(
                                                          driver?.object_id ??
                                                            ''
                                                        );
                                                      }}
                                                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                                      disabled={loadingCancelMap.get(
                                                        driver?.object_id
                                                      )}
                                                    >
                                                      {loadingCancelMap.get(
                                                        driver?.object_id
                                                      ) ? (
                                                        <ClipLoader
                                                          color="#1D213B"
                                                          loading={true}
                                                          size={16}
                                                          aria-label="Loading Spinner"
                                                          data-testid="loader"
                                                        />
                                                      ) : (
                                                        <X size={20} />
                                                      )}
                                                      <p>
                                                        {loadingCancelMap.get(
                                                          driver?.object_id
                                                        )
                                                          ? 'Canceling...'
                                                          : 'Cancel Invite'}
                                                      </p>
                                                    </button>
                                                  </div>
                                                )}
                                              </div>,
                                              document.body
                                            )}
                                        </button>
                                      ) : (
                                        <ClipLoader
                                          color="#1D213B"
                                          loading={loadingDeleteMap.get(
                                            driver?.object_id
                                          )}
                                          size={20}
                                          aria-label="Loading Spinner"
                                          data-testid="loader"
                                          className="h-full w-full justify-center"
                                        />
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-2">
                        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <UsersThree
                            size={40}
                            className="text-graysecondary"
                          />
                        </div>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          No Drivers or Driver Invites Found
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={
                        displayLoadingDriversAndInvites || displayLoadingTrucks
                      }
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full items-center justify-center"
                    />
                  </div>
                )}
              </div>
            </div>

            <InviteDriverModal
              isOpen={isInviteModalOpen}
              onClose={() => setIsInviteModalOpen(false)}
              trucks={displayTrucks}
              trucksLoading={displayLoadingTrucks}
              onSuccess={async () => {
                // invalidate all variants of the query (userId/searchKeyword)
                await queryClient.invalidateQueries({
                  queryKey: ['driversAndInvites'],
                });
                // or force an immediate fetch of active instances:
                await queryClient.refetchQueries({
                  queryKey: ['driversAndInvites', userId],
                  type: 'active',
                });
              }}
            />
          </div>
        );
    }
  };

  const renderChangeTruckModal = () => (
    <Modal
      title="Change Truck"
      open={isChangeTruckModalOpen}
      onOk={handleChangeTruck}
      onCancel={() => setIsChangeTruckModalOpen(false)}
      okText="Update"
      cancelText="Cancel"
    >
      <div className="mb-4">
        <p className="mb-2 text-[14px] font-medium text-primary">
          Select Truck
        </p>
        <Select
          placeholder="Select a truck"
          style={{ width: '100%' }}
          onChange={(value) => setSelectedTruckId(value)}
          value={selectedTruckId || undefined}
          className="w-full"
        >
          {displayTrucks.map((truck: Truck) => (
            <Select.Option key={truck.truck_id} value={truck.truck_id}>
              {truck.truck_display_name}
            </Select.Option>
          ))}
        </Select>
      </div>
    </Modal>
  );

  return (
    <>
      {renderScreenView(screenView)}
      {renderChangeTruckModal()}
    </>
  );
}

export default DriversHome;
